import React from 'react';
import { Link } from 'react-router-dom';
import {
  MapPinIcon,
  CurrencyYenIcon,
  ClockIcon,
  CalendarIcon,
  HeartIcon,
  StarIcon,
  EyeIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { calculateDistance } from '../utils/location';
import JobCountdownTimer from './JobCountdownTimer';

const JobCard = ({ job, isFavorite, onToggleFavorite, userLocation, onJobExpired }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${month}/${day}`;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('ja-JP', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const getDistance = () => {
    if (!userLocation || !job.latitude || !job.longitude) return null;
    const distance = calculateDistance(
      userLocation.lat,
      userLocation.lng,
      job.latitude,
      job.longitude
    );
    return distance < 1 ? `${(distance * 1000).toFixed(0)}m` : `${distance.toFixed(1)}km`;
  };

  const getThumbnail = () => {
    if (job.thumbnail_image) return job.thumbnail_image;
    if (job.images && job.images.length > 0) return job.images[0];
    return null;
  };

  const distance = getDistance();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* 画像部分 */}
      <div className="relative">
        {getThumbnail() ? (
          <img
            src={getThumbnail()}
            alt={job.title}
            className="w-full h-48 object-cover"
          />
        ) : (
          <div className="w-full h-48 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
            <div className="text-center">
              <UserIcon className="h-12 w-12 text-blue-400 mx-auto mb-2" />
              <p className="text-sm text-blue-600 font-medium">{job.category}</p>
            </div>
          </div>
        )}
        
        {/* お気に入りボタン */}
        <button
          onClick={(e) => {
            e.preventDefault();
            onToggleFavorite(job.id);
          }}
          className="absolute top-3 right-3 p-2 bg-white/80 backdrop-blur-sm rounded-full shadow-md hover:bg-white/90 transition-colors"
        >
          {isFavorite ? (
            <HeartIconSolid className="h-5 w-5 text-red-500" />
          ) : (
            <HeartIcon className="h-5 w-5 text-gray-600" />
          )}
        </button>
        
        {/* 即日勤務可能バッジ */}
        {job.same_day_ok && (
          <div className="absolute top-3 left-3 bg-green-500 text-white text-xs font-medium px-2 py-1 rounded-full">
            即日OK
          </div>
        )}
        
        {/* 未経験OKバッジ */}
        {!job.experience_required && (
          <div className="absolute top-3 left-3 bg-blue-500 text-white text-xs font-medium px-2 py-1 rounded-full"
               style={{ marginTop: job.same_day_ok ? '28px' : '0' }}>
            未経験OK
          </div>
        )}
        
        {/* カウントダウンタイマー */}
        <JobCountdownTimer 
          job={job}
          onExpired={onJobExpired}
        />
      </div>
      
      {/* コンテンツ部分 */}
      <Link to={`/jobs/${job.id}`} className="block p-4 hover:bg-gray-50 transition-colors">
        {/* タイトルと時給 */}
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 flex-1 mr-2">
            {job.title}
          </h3>
          <div className="text-right flex-shrink-0">
            <div className="flex items-center text-lg font-bold text-green-600">
              <CurrencyYenIcon className="h-5 w-5 mr-1" />
              {job.hourly_rate.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500">時給</div>
          </div>
        </div>
        
        {/* 場所と距離 */}
        <div className="flex items-center text-sm text-gray-600 mb-2">
          <MapPinIcon className="h-4 w-4 mr-1 flex-shrink-0" />
          <span className="truncate">{job.location}</span>
          {distance && (
            <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full flex-shrink-0">
              {distance}
            </span>
          )}
        </div>
        
        {/* 勤務時間と日程 */}
        <div className="flex items-center text-sm text-gray-600 mb-2">
          <ClockIcon className="h-4 w-4 mr-1 flex-shrink-0" />
          <span>{job.work_hours || `${formatTime(job.start_date)} - ${formatTime(job.end_date)}`}</span>
          <CalendarIcon className="h-4 w-4 ml-3 mr-1 flex-shrink-0" />
          <span>{formatDate(job.start_date)}</span>
        </div>
        
        {/* 募集人数と応募状況 */}
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm text-gray-600">
            募集人数: {job.required_people}名
          </div>
          <div className="text-sm text-gray-600">
            応募: {job.current_applicants}/{job.max_applicants}名
          </div>
        </div>
        
        {/* 事業者情報と評価 */}
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-700 font-medium">
            {job.employer_name}
          </div>
          <div className="flex items-center">
            <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
            <span className="text-sm text-gray-600">
              {job.employer_rating ? job.employer_rating.toFixed(1) : 'ー'}
            </span>
          </div>
        </div>
        
        {/* 応募状況インジケーター */}
        <div className="mt-3">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                job.current_applicants >= job.max_applicants
                  ? 'bg-red-500'
                  : job.current_applicants / job.max_applicants > 0.7
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              }`}
              style={{
                width: `${Math.min((job.current_applicants / job.max_applicants) * 100, 100)}%`
              }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>応募状況</span>
            <span>
              {job.current_applicants >= job.max_applicants
                ? '募集終了'
                : `残り${job.max_applicants - job.current_applicants}名`
              }
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default JobCard;